/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/label-has-for */
import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Form, Input } from '@rocketseat/unform';
import * as Yup from 'yup';
import MailCheck from 'react-mailcheck';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import ReCAPTCHA from 'react-google-recaptcha';
import AuthLayout from '../../../pages/_layouts/auth';
import { useAppContext } from '../../../context/AppContext';

const cpfRegex = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/;
// CNPJ (inclusive alfanumérico): XX.XXX.XXX/XXXX-00 ou sem máscara
const cnpjRegex = /^[0-9A-Z]{2}\.?[0-9A-Z]{3}\.?[0-9A-Z]{3}\/?[0-9A-Z]{4}-?[0-9]{2}$/i;

// CPF só com o parâmetro PRECADASTRO_ACEITA_CPF ligado
const schemaFor = aceitaCpf =>
  Yup.object().shape({
    cnpj: Yup.string()
      .required(aceitaCpf ? 'Por favor, insira um CNPJ ou CPF.' : 'Por favor, insira um CNPJ.')
      .test('cpf-cnpj', aceitaCpf ? 'CPF ou CNPJ inválido!' : 'CNPJ inválido!', function(value) {
        if (!value) return false;

        return cnpjRegex.test(value) || (aceitaCpf && cpfRegex.test(value));
      }),

    email: Yup.string()
      .email()
      .required('Por favor, insira um e-mail.'),
  });

const Step1 = props => {
  const { siteConfig, aceitaCpf } = useAppContext();
  const { userData, onSetUserData, currentStep } = props;
  const [email, setEmail] = useState(props.userData.email || '');
  const [cnpj, setCnpj] = useState(maskDocumento(props.userData.cnpj));
  const [message, setMessage] = useState({});
  const [loading, setLoading] = useState(false);
  const [errCode, setError] = useState(null);
  const [termsChecked, setTermsChecked] = useState(false);

  const recaptchaRef = React.useRef();

  const handleSubmit = async formData => {
    setMessage('');
    setError(null);

    let captchaToken = null;
    const validacaoRecaptcha = await api.post('/auth/validacao-recaptcha');

    if (!validacaoRecaptcha.data && recaptchaRef.current) {
      captchaToken = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();
    }
    const { cnpj, email } = formData;

    try {
      setLoading(true);

      const csrf = await api.get('/sanctum/csrf-cookie');

      const response = await api.post('auth/precadastro', {
        documento: cnpj,
        captchaToken: captchaToken,
        email,
      });

      const { error, mensagem, dados, errCode } = response.data;

      if (error !== 1) {
        setLoading(false);
        setMessage({ type: 1, msg: mensagem });

        if (dados) {
          // Espaços em branco vindos da consulta não podem virar dado "preenchido":
          // whitespace-only -> undefined, para o campo ficar editável e cair no required.
          const clean = v =>
            typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;

          const userDataReceived = {
            email,
            nome: clean(dados.nomerazao),
            fantasia: clean(dados.fantasia),
            rgie: clean(dados.rgie),
            cnpj: clean(dados.cnpj),
            cep: clean(dados.cep),
            endereco: clean(dados.logradouro),
            numero: clean(dados.numero),
            complemento: clean(dados.complemento),
            bairro: clean(dados.bairro),
            cidade: clean(dados.cidade),
            estado: clean(dados.uf),
            dadosinseridos: dados.dadosinseridos
              ? dados.dadosinseridos
              : undefined,
            nomesocio1: clean(dados.nomesocio1),
            cnpjcpfsocio1: clean(dados.cnpjcpfsocio1),
            nomesocio2: clean(dados.nomesocio2),
            cnpjcpfsocio2: clean(dados.cnpjcpfsocio2),
            nomesocio3: clean(dados.nomesocio3),
            cnpjcpfsocio3: clean(dados.cnpjcpfsocio3),
          };

          onSetUserData(userDataReceived);

          setMessage('');

          // Jump to the next step if everything is okay
          const { jumpToStep } = props;
          jumpToStep(currentStep + 1);
        }
      } else {
        setError(errCode);

        // console.log(errCode);

        // eslint-disable-next-line no-throw-literal
        throw { err: mensagem };
      }
    } catch (err) {
      setMessage({
        type: 2,
        msg:
          err.err ||
          'Ops! Ocorreu um erro ao analisar seus dados. Por favor, tente novamente.',
      });
    }

    setLoading(false);
  };

  function handleClickMailcheck(suggestion) {
    setEmail(suggestion.full);
  }

  function handleChange(event) {
    setEmail(event.target.value);
  }

  function maskDocumento(documento) {
    if (!documento) {
      return '';
    }
    const raw = documento.toUpperCase().replace(/[^0-9A-Z]/g, ''); // só alfanuméricos

    // CPF: até 11 dígitos, só números
    if (aceitaCpf && raw.length <= 11 && /^\d*$/.test(raw)) {
      return raw
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
    }

    // CNPJ (inclusive alfanumérico): letra presente ou acima de 11 caracteres
    return raw
      .replace(/([0-9A-Z]{2})([0-9A-Z])/, '$1.$2')
      .replace(/([0-9A-Z]{3})([0-9A-Z])/, '$1.$2')
      .replace(/([0-9A-Z]{3})([0-9A-Z])/, '$1/$2')
      .replace(/([0-9A-Z]{4})([0-9A-Z])/, '$1-$2')
      .replace(/(-[0-9A-Z]{2})[0-9A-Z]+?$/, '$1');
  }

  function handleChangeCnpj(event) {
    setCnpj(maskDocumento(event.target.value));
  }

  return (
    <AuthLayout>
      <div className="step step3">
        <div className="row">
          <span>Faça o seu cadastro e aproveite!</span>
          <br />
          <br />
          <Form
            schema={schemaFor(aceitaCpf)}
            onSubmit={handleSubmit}
            className="form"
            initialData={{ cnpj: userData.cnpj, email: userData.email }}
          >
            <label htmlFor="cnpj">{aceitaCpf ? 'CNPJ ou CPF*' : 'CNPJ*'}</label>
            <Input
              id="cnpj"
              placeholder={aceitaCpf ? 'Seu CNPJ ou CPF' : 'Seu CNPJ'}
              name="cnpj"
              maxLength="18"
              className="form-control"
              disabled={loading}
              inputMode="text"
              onChange={handleChangeCnpj}
              value={cnpj}
            />

            <label htmlFor="email">Email*</label>
            <MailCheck email={email}>
              {suggestion => (
                <>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email do cliente"
                    className="form-control"
                    onChange={handleChange}
                    value={email}
                    required
                    autoComplete="email"
                    disabled={loading}
                    inputMode="email"
                  />

                  {suggestion && (
                    <div className="mailcheck">
                      Você quis dizer{' '}
                      <button
                        type="button"
                        className="mailcheck-btn"
                        onClick={() => handleClickMailcheck(suggestion)}
                      >
                        {suggestion.full}
                      </button>
                      ?
                    </div>
                  )}
                </>
              )}
            </MailCheck>

            <div>
              <input
                type="checkbox"
                name="terms"
                id="terms"
                checked={termsChecked}
                onChange={e => setTermsChecked(e.target.checked)}
                required
              />
              <label className="terms" htmlFor="terms">
                Ao criar a minha conta estou de acordo com a
                <a
                  target="_blank"
                  href={siteConfig?.app_politica_privacidade_url}
                >
                  <b> política de privacidade</b>
                </a>
              </label>
            </div>

            <div>
              <span
                style={{
                  color:
                    message.type === 1
                      ? '#00C853'
                      : message.type === 2
                      ? '#dc8500'
                      : '',
                  paddingTop: '10px',
                }}
              >
                {message.msg}
              </span>
            </div>

            <div style={{ margin: '10px auto' }}>
              {!!siteConfig?.app_recaptcha_key_v2 && (
                <ReCAPTCHA
                  sitekey={siteConfig?.app_recaptcha_key_v2}
                  badge="bottomright"
                  ref={recaptchaRef}
                  size="invisible"
                  hl={'pt-BR'}
                />
              )}
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Carregando...' : 'Criar meu cadastro'}
            </button>
            <Link to="/">
              Já tem uma conta? <b>Faça login</b>
            </Link>
          </Form>
        </div>
      </div>
    </AuthLayout>
  );
};

Step1.propTypes = {
  userData: PropTypes.object.isRequired,
  onSetUserData: PropTypes.func.isRequired,
  jumpToStep: PropTypes.func,
  currentStep: PropTypes.number.isRequired,
};

Step1.defaultProps = {
  jumpToStep: () => {},
  // userData: () => {},
};

export default Step1;
